import { ColliStateName } from '@frontend/colli/types';
import { ICONS, Package } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { OnboardScanner } from '@frontend/scanner';
import { SlotBadge } from '@frontend/slot/badge';
import { SpotBadge } from '@frontend/spot/badge';
import { LuCheck, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import usePickUpScanParcels from './pick-up-scan-parcels.controller';

const ID = 'pick-up-scan-parcels';
const completedStates = [ColliStateName.COLLI_COMPLETED, ColliStateName.COLLI_COMPLETED_WITH_FAILURES];

const PickUpScanParcels = () => {
    const viewProps = usePickUpScanParcels();

    return (
        <WizardScreenContainer
            id={ID}
            header={{ title: CommonMessage.BUTTONS.PICK_UP }}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled
            }}
            loadingModal={{ show: viewProps.resolving }}
            errorModal={{
                show: !!viewProps.error,
                title: CommonMessage.ERRORS.UNRECOGNIZED_CODE,
                message: viewProps.error,
                onClose: viewProps.resetError
            }}
            containerClassName='bg-background-default flex flex-1 flex-col items-start justify-start gap-2 self-stretch overflow-y-scroll p-0'>
            <div className='h-1/4 w-full'>
                <OnboardScanner callback={viewProps.resolveScan} />
            </div>
            <div className='flex flex-1 flex-col items-start justify-start gap-2 self-stretch px-5 pt-6 pb-20'>
                <div className='mb-2 flex items-start justify-start self-stretch'>
                    <div className='text-foreground-default justify-start text-base font-medium'>
                        {viewProps.expectedCollis?.length === 1 ? (
                            <FormattedMessage
                                id='pick-up-scan-parcels.scan-and-take-parcel'
                                defaultMessage='Take your parcel from the location listed below and scan the parcel-code to complete your pickup'
                            />
                        ) : (
                            <FormattedMessage
                                id='pick-up-scan-parcels.scan-and-take-parcels'
                                defaultMessage='Take your parcels from the locations listed below and scan the parcel-codes to complete your pickup'
                            />
                        )}
                    </div>
                </div>

                <div className='flex w-full flex-col gap-2'>
                    {viewProps.expectedCollis?.map((expectedColli) => {
                        const isAlreadyCompleted = completedStates.includes(expectedColli.state);
                        const atOtherLocation = viewProps.isColliAtOtherLocation(expectedColli);
                        const isScanned = viewProps.scannedCollis.some((s) => s.id === expectedColli.id);
                        const slotId = expectedColli.current_slot_id || expectedColli.slot_id || undefined;

                        return (
                            <div
                                key={expectedColli.id}
                                className={`bg-background-surface flex w-full flex-col items-start justify-start self-stretch rounded-xl p-4 ${isAlreadyCompleted || atOtherLocation ? 'opacity-60' : ''}`}>
                                <div className='flex w-full items-start gap-4'>
                                    <div className='outline-border-default relative flex size-12 shrink-0 items-center justify-center rounded-full outline-2'>
                                        {isScanned ? (
                                            <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                        ) : (
                                            <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                        )}
                                        <Package className='size-1/2' />
                                    </div>

                                    <div className='flex min-w-0 flex-1 flex-col items-start justify-center gap-1'>
                                        <div className='text-foreground-default text-base font-semibold'>
                                            {CommonMessage.OBJECTS.PARCEL.DEFAULT} ({expectedColli.type})
                                        </div>
                                        <div className='text-brand-muted text-base font-medium'>{expectedColli.label}</div>
                                        {isAlreadyCompleted && (
                                            <div className='text-status-warning text-sm font-medium'>
                                                <FormattedMessage
                                                    id='mobile.wizard.pick-up-scan-parcels.already-picked-up'
                                                    defaultMessage='Already picked up'
                                                    description='Label shown on a parcel that is already in a completed state'
                                                />
                                            </div>
                                        )}
                                        {!isAlreadyCompleted && atOtherLocation && expectedColli.current_spot_id && (
                                            <div className='text-brand-muted text-sm font-medium'>
                                                <FormattedMessage
                                                    id='mobile.wizard.pick-up-scan-parcels.at-other-location'
                                                    defaultMessage='Currently at'
                                                    description='Label prefix shown when a parcel to pick up is at a different spot than the device location'
                                                />{' '}
                                                <SpotBadge
                                                    id={expectedColli.current_spot_id}
                                                    className='ms-0 inline ps-0'
                                                />
                                            </div>
                                        )}
                                        {!isAlreadyCompleted && !atOtherLocation && slotId && (
                                            <div className='text-brand-muted text-base font-medium'>
                                                {CommonMessage.OBJECTS.SLOT.DEFAULT}{' '}
                                                <SlotBadge
                                                    id={slotId}
                                                    className='ms-0 ps-0'
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className='flex size-12 shrink-0 items-center justify-center'>
                                        {isScanned && (
                                            <button
                                                type='button'
                                                id='remove-pin'
                                                className='inline-flex size-12 items-center justify-center'
                                                onClick={() => viewProps.removeScan(expectedColli.id)}>
                                                <ICONS.BUTTON.DELETE className='text-brand-muted size-6' />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </WizardScreenContainer>
    );
};

export default PickUpScanParcels;
